import React, {FC, ReactNode, SyntheticEvent, useCallback, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {AsyncHandlerOf, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../libs/shared/ui/src/styles/mixins';

/*
 * Props.
 */

interface AvatarEditorProps {
  /** Avatar element. */
  avatar: ReactNode;
  isDisabled?: boolean;
  /** Input change handler. */
  onFileChange: HandlerOf<File> | AsyncHandlerOf<File>;
  /** Whether the controls should always be hidden (e.g. in read-only mode). */
  shouldNotShowControls?: boolean;
  /** Optional handler for errors. */
  onError?: HandlerOf<Error>;
}

/*
 * Styles.
 */

interface StyledAvatarEditorDivProps {
  $isDisabled?: boolean;
}
const StyledAvatarEditorDiv = styled.div<StyledAvatarEditorDivProps>`
  position: relative;
  border-radius: 50%;
  margin-right: 12px;

  ${(p) =>
    p.$isDisabled &&
    css`
      opacity: 0.5;
    `}
`;

const StyledFileLabel = styled.label`
  display: block;

  input {
    display: none;
  }
`;

const StyledEditOverlayDiv = styled.div`
  ${center()}
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: ${(p) => p.theme.alphas.black50};
  border-radius: 50%;
  color: ${(p) => p.theme.greys.white};
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  pointer-events: none;
`;

/*
 * Component.
 */

export const AvatarEditor: FC<AvatarEditorProps> = (props) => {
  const {avatar, onFileChange, shouldNotShowControls, onError, isDisabled} = props;

  const [shouldShowControls, setShouldShowControls] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [inputKey, setInputKey] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showControls = () => {
    if (shouldNotShowControls || isSaving) {
      return;
    }

    setShouldShowControls(true);
  };

  const hideControls = () => {
    if (shouldShowControls) {
      setShouldShowControls(false);
    }
  };

  const onChange = useCallback(
    async (event: SyntheticEvent<HTMLInputElement>) => {
      // Only accept the first file, we should only get one
      const {files} = event.currentTarget;
      const fileList = files && Array.from(files);
      if (fileList && fileList.length > 0) {
        setIsSaving(true);
        try {
          await onFileChange(fileList[0]);
        } catch (error) {
          if (onError) {
            onError(error);
          }
        }
        setIsSaving(false);
      }

      // Re-create the input to avoid existing-value issues.
      setInputKey((value) => value + 1);
    },
    [onFileChange, setInputKey, onError],
  );

  const onAddFileClick = useCallback(() => {
    if (!fileInputRef.current || isDisabled) {
      return;
    }
    fileInputRef.current.click();
  }, [isDisabled]);

  return (
    <StyledAvatarEditorDiv
      $isDisabled={isDisabled}
      onMouseEnter={showControls}
      onMouseMove={showControls}
      onMouseLeave={hideControls}
      onClick={onAddFileClick}
    >
      {avatar}
      {shouldShowControls && !isDisabled && (
        <StyledEditOverlayDiv>
          <Trans id="avatar-editor-edit">Edit</Trans>
        </StyledEditOverlayDiv>
      )}
      <StyledFileLabel>
        <input
          ref={fileInputRef}
          key={inputKey}
          disabled={isDisabled}
          type="file"
          accept="image/*"
          onChange={onChange}
        />
      </StyledFileLabel>
    </StyledAvatarEditorDiv>
  );
};
